import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../services/api";
import { normalizarYoutubeWatch } from "../utils/youtube";
import { normalizarLinkExterno, normalizarUrlArquivo } from "../utils/projetos";
import { normalizarProjetoBackend } from "../utils/projetos";
import "../styles/admin.css";

function AdminPreview({ apresentacao, titulo }) {
  if (!apresentacao?.arquivo) return null;

  return (
    <div className="admin-preview">
      <img src={normalizarUrlArquivo(apresentacao.arquivo)} alt={titulo || "Miniatura"} />
    </div>
  );
}

const formVazio = {
  titulo: "",
  descricao: "",
  info: "",
  tipo: "RV",
  apresentacao: { tipo: "imagem", arquivo: "", url: "" },
  tags: [],
  disponibilidade: [],
};

function AdminForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editId = id ? Number(id) : null;

  const [form, setForm] = useState(formVazio);
  const [tagTemp, setTagTemp] = useState("");
  const [dispTemp, setDispTemp] = useState({ tipo: "Youtube", link: "" });
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(function () {
    if (!editId) return;

    let ativo = true;

    async function carregarItem() {
      setCarregando(true);
      try {
        const response = await api.get(`/cards/${editId}`);
        if (!ativo) return;

        const item = normalizarProjetoBackend(response.data, { admin: true });
        setForm({
          titulo: item.titulo || "",
          descricao: item.descricao || "",
          info: item.info || "",
          tipo: item.tipo || "RV",
          apresentacao: { tipo: "imagem", arquivo: item.apresentacao?.arquivo || "", url: "" },
          tags: Array.isArray(item.tags) ? [...item.tags] : [],
          disponibilidade: Array.isArray(item.disponibilidade) ? [...item.disponibilidade] : [],
        });
      } catch (error) {
        console.error("Erro ao carregar item:", error);
        setErro("Nao foi possivel carregar este card para edicao.");
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    carregarItem();

    return function () {
      ativo = false;
    };
  }, [editId]);

  async function ensureTagIds(tagNames) {
    const normalizedNames = Array.from(
      new Set((tagNames || []).map((name) => name.trim()).filter(Boolean))
    );

    if (normalizedNames.length === 0) return [];

    const response = await api.get("/tags");
    const existingTags = response.data || [];
    const existingByName = new Map(
      existingTags.map((tag) => [tag.name.toLowerCase(), tag])
    );

    const tagIds = [];

    for (const name of normalizedNames) {
      const key = name.toLowerCase();
      if (existingByName.has(key)) {
        tagIds.push(existingByName.get(key).id);
        continue;
      }

      const createResponse = await api.post("/tags", { name });
      const newTag = createResponse.data;
      tagIds.push(newTag.id);
      existingByName.set(newTag.name.toLowerCase(), newTag);
    }

    return tagIds;
  }

  function buildPayload(tagIds = []) {
    const disponibilidade = form.disponibilidade.map((item) => {
      if (item.tipo === "Youtube") {
        return {
          ...item,
          link: normalizarYoutubeWatch(item.link || item.url || ""),
        };
      }
      return {
        ...item,
        link: normalizarLinkExterno(item.link || item.url || ""),
      };
    });

    return {
      title: form.titulo,
      description: form.descricao,
      info: form.info,
      content: JSON.stringify({
        tipo: form.tipo,
        apresentacao: form.apresentacao,
        tags: form.tags,
        disponibilidade,
      }),
      tags: tagIds,
    };
  }

  async function uploadFile(file) {
    if (!file) return "";
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data.url;
    } catch (error) {
      console.error("Erro no upload do arquivo:", error);
      setErro("Nao foi possivel enviar o arquivo. Tente novamente.");
      setTimeout(() => setErro(""), 5000);
      return "";
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!form.titulo.trim() || !form.descricao.trim() || !form.info.trim()) {
      setErro("Preencha todos os campos obrigatorios!");
      setTimeout(() => setErro(""), 5000);
      return;
    }

    if (!form.apresentacao.arquivo) {
      setErro("Selecione uma imagem para o card.");
      setTimeout(() => setErro(""), 5000);
      return;
    }

    setErro("");
    setSucesso("");
    setCarregando(true);

    try {
      const tagIds = await ensureTagIds(form.tags);
      const payload = buildPayload(tagIds);

      if (editId) {
        await api.put(`/cards/${editId}`, payload);
        setSucesso(`Card "${form.titulo}" atualizado com sucesso!`);
      } else {
        await api.post("/cards", payload);
        setSucesso(`Card "${form.titulo}" criado com sucesso!`);
      }

      setTimeout(() => navigate("/admin"), 650);
    } catch (error) {
      console.error("Erro ao salvar item:", error);
      const mensagemErro = error.response?.data?.message || error.message || "Erro desconhecido";
      setErro(`Erro ao salvar: ${mensagemErro}`);
      setTimeout(() => setErro(""), 5000);
    } finally {
      setCarregando(false);
    }
  }

  function adicionarTag() {
    if (tagTemp.trim()) {
      setForm((f) => ({ ...f, tags: [...f.tags, tagTemp.trim()] }));
      setTagTemp("");
    }
  }

  function removerTag(index) {
    setForm((f) => ({ ...f, tags: f.tags.filter((_, i) => i !== index) }));
  }

  function adicionarDisponibilidade() {
    if (dispTemp.link.trim()) {
      const linkNormalizado =
        dispTemp.tipo === "Youtube"
          ? normalizarYoutubeWatch(dispTemp.link)
          : normalizarLinkExterno(dispTemp.link);

      setForm((f) => ({
        ...f,
        disponibilidade: [...f.disponibilidade, { ...dispTemp, link: linkNormalizado }],
      }));
      setDispTemp({ tipo: "Youtube", link: "" });
    }
  }

  function removerDisponibilidade(index) {
    setForm((f) => ({
      ...f,
      disponibilidade: f.disponibilidade.filter((_, i) => i !== index),
    }));
  }

  return (
    <div className="admin-container">
      <div className="admin-content admin-form-page">
        {sucesso && <div className="notification success">{sucesso}</div>}
        {erro && <div className="notification error">{erro}</div>}

        <div className="admin-page-header">
          <div>
            <span className="admin-eyebrow">Admin</span>
            <h1>{editId ? "Editar card" : "Novo card"}</h1>
          </div>
          <button type="button" className="btn-voltar" onClick={() => navigate("/admin")}>
            Voltar para lista
          </button>
        </div>

        <form className="admin-form" onSubmit={handleSubmit}>
          <label>Titulo*</label>
          <input
            type="text"
            value={form.titulo}
            onChange={(e) => setForm({ ...form, titulo: e.target.value })}
            placeholder="Titulo do projeto"
            required
          />

          <label>Descrição*</label>
          <textarea
            value={form.descricao}
            onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            required
            placeholder="Descricao resumida"
            maxLength={100}
          />

          <label>Descrição Completa*</label>
          <textarea
            value={form.info}
            onChange={(e) => setForm({ ...form, info: e.target.value })}
            placeholder="Descricao mais completa do projeto"
            required
          />

          <label>Tipo*</label>
          <select
            value={form.tipo}
            onChange={(e) => setForm({ ...form, tipo: e.target.value })}
            required
          >
            <option value="RV">Realidade Virtual</option>
            <option value="RA">Realidade Aumentada</option>
            <option value="Video 360">Video 360</option>
            <option value="Video 360i">Video 360i</option>
          </select>

          <fieldset>
            <label>Imagem de apresentação</label>
            <select value="imagem" disabled>
              <option value="imagem">Imagem</option>
            </select>

            <label>Arquivo</label>
            <div className="admin-file-row">
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const url = await uploadFile(file);
                    if (url) {
                      setForm({
                        ...form,
                        apresentacao: { tipo: "imagem", arquivo: url, url: "" },
                      });
                    }
                  }
                }}
              />
              <span>
                {form.apresentacao.arquivo
                  ? `Selecionado: ${form.apresentacao.arquivo.split("/").pop()}`
                  : "Selecione um arquivo"}
              </span>
            </div>

            <AdminPreview apresentacao={form.apresentacao} titulo={form.titulo} />
          </fieldset>

          <fieldset>
            <label>Tags</label>
            <div className="tag-input-group">
              <input
                type="text"
                value={tagTemp}
                onChange={(e) => setTagTemp(e.target.value)}
              />
              <button type="button" onClick={adicionarTag}>Adicionar tag</button>
            </div>
            <ul className="tag-list">
              {form.tags.map((t, i) => (
                <li key={i} className="tag-chip">
                  <span className="tag-chip-text">{t}</span>
                  <button
                    type="button"
                    className="chip-delete"
                    aria-label={`Remover tag ${t}`}
                    title="Remover tag"
                    onClick={() => removerTag(i)}
                  />
                </li>
              ))}
            </ul>
          </fieldset>

          <fieldset>
            <label>Disponibilidade</label>
            <div className="disp-input-group">
              <select
                value={dispTemp.tipo}
                onChange={(e) => setDispTemp((d) => ({ ...d, tipo: e.target.value }))}
              >
                <option>Youtube</option>
                <option>APK</option>
                <option>RD</option>
                <option>Download</option>
                <option>Space</option>
              </select>
              <input
                type="text"
                placeholder="Link"
                value={dispTemp.link}
                onChange={(e) => setDispTemp((d) => ({ ...d, link: e.target.value }))}
              />
              <button type="button" onClick={adicionarDisponibilidade}>Adicionar</button>
            </div>
            <ul className="disp-list">
              {form.disponibilidade.map((d, i) => (
                <li key={i} className="disp-item">
                  <span className="disp-type">{d.tipo}</span>
                  <span className="disp-link">{d.link}</span>
                  <button
                    type="button"
                    className="chip-delete"
                    aria-label={`Remover disponibilidade ${d.tipo}`}
                    title="Remover disponibilidade"
                    onClick={() => removerDisponibilidade(i)}
                  />
                </li>
              ))}
            </ul>
          </fieldset>

          <div className="form-actions" style={{ justifyContent: "center" }}>
            <button type="submit" disabled={carregando || uploading}>
              {uploading ? "Enviando arquivo..." : editId ? "Salvar" : "Criar"}
            </button>
            <button type="button" onClick={() => navigate("/admin")} disabled={uploading}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminForm;
